import type { HaloSummary } from '@halo/web-types';
import { GATE_REASON_COPY } from '@halo/web-types';

const cardCls =
  'rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm';

interface MixCardProps {
  summary: HaloSummary | undefined;
  isLoading: boolean;
}

export function MixCard({ summary, isLoading }: MixCardProps) {
  return (
    <div className={cardCls}>
      <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Destination mix</h3>
      {isLoading || !summary ? (
        <p className="text-gray-400 text-xs">Loading...</p>
      ) : summary.attributableShares ? (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Attributable mix · est. $
            {Math.round(summary.total.attributableHalo ?? 0).toLocaleString()}
          </p>
          {Object.entries(summary.attributableShares)
            .sort(([, a], [, b]) => b - a)
            .map(([platform, share]) => (
              <div key={platform} className="flex justify-between py-0.5">
                <span className="text-gray-600 dark:text-gray-300">{platform}</span>
                <span className="font-semibold tabular-nums">{(share * 100).toFixed(1)}%</span>
              </div>
            ))}
        </>
      ) : (
        <GrossFallback summary={summary} />
      )}
    </div>
  );
}

/** Attribution gated for every platform — show gross shares, clearly labeled. */
function GrossFallback({ summary }: { summary: HaloSummary }) {
  const grossTotal = summary.total.grossPositiveHalo;
  const reasons = [
    ...new Set(
      Object.values(summary.perPlatform)
        .map((r) => (r.gateReason ? GATE_REASON_COPY[r.gateReason] : null))
        .filter((r): r is string => r !== null),
    ),
  ];
  if (grossTotal <= 0) return <p className="text-gray-400 text-xs">No positive halo in range.</p>;
  return (
    <>
      <p className="text-xs text-amber-600 dark:text-amber-500 mb-2">
        Gross mix (attribution gated: {reasons.join('; ') || 'unavailable'})
      </p>
      {Object.entries(summary.perPlatform)
        .filter(([, r]) => r.grossPositiveHalo > 0)
        .sort(([, a], [, b]) => b.grossPositiveHalo - a.grossPositiveHalo)
        .map(([platform, r]) => (
          <div key={platform} className="flex justify-between py-0.5">
            <span className="text-gray-600 dark:text-gray-300">{platform}</span>
            <span className="font-semibold tabular-nums">
              {((r.grossPositiveHalo / grossTotal) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
    </>
  );
}
